import {useMediaQuery} from '@mui/material'
import {FC, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {IS_INTERESTS_ASKED, setInterestsAsked} from 'entities/app'
import {ChangeInterests, setUserInterests, useUser} from 'entities/user'
import {
  GetInterests,
  interestsCloseModal,
  interestsOpenModal,
  useInterestsData,
  useInterestsIsOpen
} from 'entities/user/model/interests'
import {names} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {Cross, Gamepad} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import {Modal} from 'shared/ui/modal'
import {Ref} from 'shared/ui/ref'
import sharedStyles from 'shared/ui/styles/authModals.module.scss'
import {InterestsModalProps, convertToDataArray, convertToIdArray} from './helper'
import styles from './styles.module.scss'
import {InterestsSelect} from './ui/interestsSelect'

const maxInterestsLength = 30

export const InterestsModal: FC<InterestsModalProps> = ({canSkip}) => {
  const dispatch = useDispatch()
  const user = useUser()
  const [selectedIds, setSelectedIds] = useState<number[]>(convertToIdArray(user.profile.interests))
  const isOpen = useInterestsIsOpen()
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const isMobileMedium = useMediaQuery(`(max-width: ${styles.isMobileMedium})`)
  const [isSaving, setIsSaving] = useState(false)
  const interestsCategories = useInterestsData()
  const {refetch, isLoading} = GetInterests(dispatch)

  const onClose = () => dispatch(interestsCloseModal())

  const onSaveHandler = () => {
    if (isSaving) return
    setIsSaving(true)

    ChangeInterests(selectedIds)
      .then(() => {
        dispatch(setUserInterests(convertToDataArray(selectedIds, interestsCategories)))
        onClose()
      })
      .catch(console.error)
      .finally(() => setIsSaving(false))
  }

  const onChangeInterest = (interestId: number) => {
    if (selectedIds.length >= maxInterestsLength && !selectedIds.includes(interestId)) return

    return selectedIds.includes(interestId)
      ? setSelectedIds(selectedIds.filter((id) => id !== interestId))
      : setSelectedIds([...selectedIds, interestId])
  }

  const onSkip = () => {
    dispatch(setInterestsAsked(true))
    onClose()
  }

  useEffect(() => {
    if (isOpen) refetch().catch(console.error)
  }, [isOpen])

  useEffect(() => {
    const isInterestsAsked = localStorage.getItem(IS_INTERESTS_ASKED)

    if (!user.profile.interests.length && !(isInterestsAsked && JSON.parse(isInterestsAsked).value)) {
      dispatch(interestsOpenModal())
    }
  }, [user.profile.interests])

  return (
    <Modal
      open={isOpen}
      disableAutoFocus
      disableEnforceFocus
      onClose={onClose}
    >
      <div className={styles.containerWrapper}>
        <div className={names(sharedStyles.modal, styles.container)}>
          {isMobileMedium
            ? <div className={styles.header}>
              <div className={styles.closeBtn} onClick={onClose}>
                <Cross />
              </div>
              <p className={styles.title}>Interests</p>
            </div>
            : <div className={names(sharedStyles.modal__titleContainer, styles.titleContainer)}>
              <div className={sharedStyles.modal__titleIcon}>
                <Gamepad />
              </div>
              <div className={sharedStyles.modal__textContainer}>
                <p className={sharedStyles.modal__title}>Interests</p>
                <span className={sharedStyles.modal__title__description}>
                  It's almost done!
                </span>
              </div>
              {!canSkip
                ? <div className={styles.closeBtn} onClick={onClose}>
                  <Cross />
                </div>
                : null}
            </div>
          }
          <div className={styles.body}>
            {isLoading ?
              <div className={styles.loader}><Loader /></div>
              :
              <InterestsSelect
                interestCategories={interestsCategories}
                selectedInterestsIds={selectedIds}
                onChangeInterest={onChangeInterest}
              />
            }
          </div>
          <div className={styles.footer}>
            <p className={styles.footerTitle}>
              {is768p
                ? <><b>{selectedIds.length}</b> selected</>
                : <>You have selected <b>{selectedIds.length}</b> interest</>
              }
            </p>
            {canSkip && !selectedIds.length
              ? <Ref className={styles.btnSkip} asDiv onClick={onSkip}>Skip</Ref>
              : <Button styleType='primary' onClick={onSaveHandler} className={styles.btnSave}>
                {isSaving ? <Loader innerColor='white' /> : canSkip ? 'Continue' : 'Save'}
              </Button>}
          </div>
        </div>
      </div>
    </Modal>
  )
}
